import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import {
    Pagination,
    PaginationItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';
import Loading from '../../components/common/Loading';
import PawUI from '../../components/ui/PawUI';
import { Request } from '../../config/requests';
import { getToken } from '../../config/token';
import { showToast } from '../../config/helpers';

const PAGE_SIZES = [25, 50, 100];

const STATUS = {
    ALL: 'all',
    UNCONFIRMED: 'unconfirmed',
    CONFIRMED: 'confirmed',
};

const getLastCommentPart = (comment) => {
    if (!comment) return '';
    const parts = comment.split(';');
    return parts[parts.length - 1].trim();
};

const formatDate = (value) => {
    if (!value) return '—';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (isNaN(d)) return '—';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export default function DunningsPage() {
    const { t } = useTranslation();
    const token = getToken();

    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25, pageCount: 0, total: 0 });
    const [summary, setSummary] = useState({ open: 0, confirmed: 0 });

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [status, setStatus] = useState(STATUS.ALL);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    // documentIds currently being written, so only that row's control is disabled
    const [pending, setPending] = useState(new Set());

    // Debounce the search box so typing a policy number is not one request per keystroke.
    useEffect(() => {
        const id = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(id);
    }, [searchInput]);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
                status,
            });
            if (search) params.set('search', search);

            const res = await Request({
                method: 'GET',
                route: `api/dunnings/crons?${params.toString()}`,
                token,
            });
            const data = await res.json();

            if (!res.ok) {
                showToast(data.message || data.error || t('somethingWentWrong'), { error: true });
                return;
            }

            setRows(data.items || []);
            if (data.pagination) setPagination(data.pagination);
            if (data.summary) setSummary(data.summary);
        } catch (err) {
            showToast(err.message || t('somethingWentWrong'), { error: true });
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, status, search, token, t]);

    useEffect(() => {
        load();
    }, [load]);

    // A filter change can leave us past the last page; walk back rather than showing nothing.
    useEffect(() => {
        if (!isLoading && pagination.pageCount > 0 && page > pagination.pageCount) {
            setPage(pagination.pageCount);
        }
    }, [isLoading, pagination.pageCount, page]);

    const toggleConfirmed = async (row) => {
        const next = !row.is_confirmed;
        setPending((prev) => new Set(prev).add(row.documentId));

        try {
            const res = await Request({
                method: 'PATCH',
                route: `api/dunnings/crons/${row.documentId}/confirm`,
                token,
                values: { is_confirmed: next },
            });
            const data = await res.json();

            if (!res.ok) {
                showToast(data.message || data.error || t('somethingWentWrong'), { error: true });
                return;
            }

            // Only flip local state once Strapi confirmed the write.
            setRows((prev) =>
                prev.map((r) => (r.documentId === row.documentId ? { ...r, is_confirmed: next } : r))
            );
            setSummary((prev) => ({
                ...prev,
                confirmed: Math.max(0, prev.confirmed + (next ? 1 : -1)),
            }));

            // The confirmed/unconfirmed filters would no longer match this row.
            if (status !== STATUS.ALL) load();
        } catch (err) {
            showToast(err.message || t('somethingWentWrong'), { error: true });
        } finally {
            setPending((prev) => {
                const copy = new Set(prev);
                copy.delete(row.documentId);
                return copy;
            });
        }
    };

    const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1;
    const to = Math.min(pagination.page * pagination.pageSize, pagination.total);

    return (
        <div className="main-container">
            <Header />
            <div className="page-content">
                <div className="claims">
                    {isMobile ? (
                        <PawUI top="1vh" right="6vw" rotation="right" width="25vw" zIndex="0" />
                    ) : (
                        <>
                            <PawUI top="20vh" right="1vw" rotation="right" width="350px" zIndex="0" />
                            <PawUI bottom="-10vh" left="5vw" rotation="left" width="150px" zIndex="0" />
                        </>
                    )}
                </div>

                <div
                    className="dunnings-page"
                    style={{ position: 'relative', zIndex: 2, padding: '0 24px 40px' }}
                >
                    <div className="m-auto my-3 d-flex justify-content-center dark_red_title">
                        {t('dunningsPageHeader')}
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                        <input
                            className="modal_container__body__input"
                            placeholder={t('Search by policy number')}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{ flex: '1 1 220px', maxWidth: 320 }}
                        />

                        <select
                            className="modal_container__body__input"
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                setPage(1);
                            }}
                            style={{ flex: '0 1 180px' }}
                        >
                            <option value={STATUS.ALL}>{t('All')}</option>
                            <option value={STATUS.UNCONFIRMED}>{t('Not confirmed')}</option>
                            <option value={STATUS.CONFIRMED}>{t('Confirmed')}</option>
                        </select>

                        <select
                            className="modal_container__body__input"
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            style={{ flex: '0 1 140px' }}
                        >
                            {PAGE_SIZES.map((size) => (
                                <option key={size} value={size}>
                                    {size} {t('perPage')}
                                </option>
                            ))}
                        </select>

                        <button className="button_red_small__admin" onClick={load} disabled={isLoading}>
                            {t('Refresh')}
                        </button>

                        <div className="text-muted small ms-auto">
                            {t('Open')}: {summary.open} · {t('Confirmed')}: {summary.confirmed}
                        </div>
                    </div>

                    {isLoading && <Loading noText fast width="100%" scroll={false} />}

                    {!isLoading && !rows.length && (
                        <div className="my-4 text-center">{t('No open dunnings')}</div>
                    )}

                    {!isLoading && !!rows.length && (
                        <>
                            <TableContainer
                                className="reset-table-spacing"
                                sx={{
                                    position: 'relative',
                                    zIndex: 2,
                                    backgroundColor: 'var(--b-color-bg-main-1)',
                                }}
                            >
                                <Table className="SlowShow reset-table-new" size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('Policy Number')}</TableCell>
                                            <TableCell>{t('Type')}</TableCell>
                                            <TableCell>{t('Due date')}</TableCell>
                                            <TableCell>{t('Payment deadline')}</TableCell>
                                            <TableCell>{t('Cancel date')}</TableCell>
                                            <TableCell>{t('Processed')}</TableCell>
                                            <TableCell>{t('Status')}</TableCell>
                                            <TableCell>{t('Comment')}</TableCell>
                                            <TableCell align="center">{t('Confirmed')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row) => {
                                            const isPending = pending.has(row.documentId);
                                            return (
                                                <TableRow
                                                    key={row.documentId}
                                                    style={{
                                                        borderLeft: `4px solid ${row.is_confirmed ? '#7BAE7F' : '#FE4B4E'}`,
                                                    }}
                                                >
                                                    <TableCell>
                                                        {row.policy_link ? (
                                                            <a href={row.policy_link} target="_blank" rel="noreferrer">
                                                                {row.policy_no || row.policy_id}
                                                            </a>
                                                        ) : (
                                                            row.policy_no || row.policy_id || '—'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{row.payment_type || '—'}</TableCell>
                                                    <TableCell>{formatDate(row.due_date)}</TableCell>
                                                    <TableCell>{formatDate(row.payment_deadline)}</TableCell>
                                                    <TableCell>{formatDate(row.cancel_date)}</TableCell>
                                                    <TableCell>{formatDate(row.processed_date)}</TableCell>
                                                    <TableCell>{row.policy_status || '—'}</TableCell>
                                                    <TableCell style={{ maxWidth: 320 }}>
                                                        <Tooltip title={row.comment || ''} placement="top">
                                                            <span
                                                                style={{
                                                                    display: 'inline-block',
                                                                    maxWidth: 320,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    verticalAlign: 'bottom',
                                                                }}
                                                            >
                                                                {getLastCommentPart(row.comment) || '—'}
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <input
                                                            type="checkbox"
                                                            checked={row.is_confirmed}
                                                            disabled={isPending}
                                                            onChange={() => toggleConfirmed(row)}
                                                            aria-label={t('Confirmed')}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                                <div className="text-muted small">
                                    {t('Showing')} {from}–{to} {t('of')} {pagination.total}
                                </div>

                                {pagination.pageCount > 1 && (
                                    <Pagination
                                        className="reset-pagination"
                                        count={pagination.pageCount}
                                        page={pagination.page}
                                        siblingCount={1}
                                        boundaryCount={1}
                                        onChange={(event, value) => setPage(value)}
                                        renderItem={(item) => <PaginationItem {...item} />}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}
